import React from 'react';
import { Box, Typography, Container, Grid, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import RecyclingIcon from '@mui/icons-material/Recycling';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Co2Icon from '@mui/icons-material/Co2';
import PublicIcon from '@mui/icons-material/Public';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { gradients } from '../theme';
import FadeIn from './FadeIn';

const commitments = [
  { text: 'Reduce landfill waste from vehicles', icon: <RecyclingIcon /> },
  { text: 'Recover high-value precious metals', icon: <AutoAwesomeIcon /> },
  { text: 'Lower carbon footprint', icon: <Co2Icon /> },
  { text: 'Support UAE environmental initiatives', icon: <PublicIcon /> },
  { text: 'Promote responsible end-of-life vehicle management', icon: <DirectionsCarIcon /> },
];

const Sustainability: React.FC = () => {
  return (
    <Box
      sx={{
        backgroundColor: 'black',
        py: 12,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decor */}
      <Box
        sx={{
          position: 'absolute',
          top: -200,
          left: -200,
          width: 800,
          height: 800,
          background: 'radial-gradient(circle, rgba(76, 175, 80, 0.1) 0%, transparent 60%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={8} alignItems="center">
          {/* Left Side: Title */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <FadeIn direction="right">
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: '#66bb6a', fontWeight: 600, letterSpacing: 2, mb: 2, display: 'block' }}
                >
                  CLEANER FUTURE
                </Typography>
                <Typography
                  sx={{
                    ...gradients.primaryText,
                    fontSize: { xs: 36, md: 56 },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: '-1px',
                    mb: 1,
                  }}
                >
                  Sustainability
                </Typography>
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: { xs: 36, md: 56 },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: '-1px',
                    mb: 4,
                  }}
                >
                  Commitment
                </Typography>
                <Typography
                  sx={{ color: 'grey.400', fontSize: 18, maxWidth: 500, lineHeight: 1.6 }}
                >
                  We are dedicated to minimizing environmental impact through advanced recycling technologies and responsible practices that turn waste into value.
                </Typography>
              </Box>
            </FadeIn>
          </Grid>

          {/* Right Side: Cards */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Grid container spacing={3}>
              {commitments.map((item, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: index < 2 ? 6 : 4 }}>
                  <FadeIn delay={index * 0.1}>
                    <Paper
                      component={motion.div}
                      whileHover={{ y: -8, borderColor: '#66bb6a' }}
                      sx={{
                        p: 4,
                        height: '100%',
                        minHeight: 220,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        gap: 3,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          backgroundColor: 'rgba(102, 187, 106, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#66bb6a',
                        }}
                      >
                        {React.cloneElement(item.icon, { fontSize: 'large' })}
                      </Box>
                      <Typography
                        sx={{
                          color: '#e0e0e0',
                          fontSize: 16,
                          fontWeight: 500,
                          lineHeight: 1.5,
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Paper>
                  </FadeIn>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Sustainability;
